// Utility functions for calculating sectional analysis based on test configuration

/**
 * Calculate sectional results based on test configuration and user answers
 * @param {Object} testConfig - Test configuration with sections
 * @param {Object} userAnswers - User's answers object with question IDs as keys
 * @param {Array} questions - Questions array
 * @param {Object} overallResults - Overall test results
 * @returns {Array} Sectional results array
 */
export const calculateSectionalResults = (testConfig, userAnswers, questions, overallResults) => {
  if (!testConfig || !testConfig.sections || !userAnswers || !questions) {
    return generateDefaultSectionalResults(overallResults);
  }

  const sectionalResults = [];

  testConfig.sections.forEach(section => {
    const sectionResult = calculateSectionResult(section, userAnswers, questions);
    sectionalResults.push(sectionResult);
  });

  return sectionalResults;
};

/**
 * Calculate results for a specific section
 * @param {Object} section - Section configuration
 * @param {Object} userAnswers - User's answers object with question IDs as keys
 * @param {Array} questions - Questions array
 * @returns {Object} Section result
 */
const calculateSectionResult = (section, userAnswers, questions) => {
  const startIndex = parseInt(section.startQuestion) - 1; // Convert to 0-based index
  const endIndex = parseInt(section.endQuestion) - 1;
  
  let correctAnswers = 0;
  let wrongAnswers = 0;
  let unattempted = 0;
  let timeSpent = 0;
  let totalMarks = 0;

  // Calculate section results
  for (let i = startIndex; i <= endIndex && i < questions.length; i++) {
    const question = questions[i];
    const questionId = question.id || i.toString();
    
    // Handle different userAnswers structures
    let userAnswer;
    if (userAnswers.answers && userAnswers.answers[questionId] !== undefined) {
      userAnswer = userAnswers.answers[questionId];
    } else if (userAnswers[questionId] !== undefined) {
      userAnswer = userAnswers[questionId];
    } else {
      userAnswer = null;
    }
    
    if (userAnswer === null || userAnswer === undefined || userAnswer === '' || userAnswer === 'null' || userAnswer === 'undefined') {
      unattempted++;
    } else {
      const isCorrect = checkAnswerCorrectness(question, userAnswer);
      if (isCorrect) {
        correctAnswers++;
        totalMarks += question.markingScheme?.correct || 4;
      } else {
        wrongAnswers++;
        totalMarks += question.markingScheme?.incorrect || -1;
      }
    }
    
    // Add time spent on this question
    if (userAnswers.timeSpent && userAnswers.timeSpent[questionId]) {
      timeSpent += userAnswers.timeSpent[questionId];
    }
  }

  const attempted = correctAnswers + wrongAnswers;
  const totalQuestions = endIndex - startIndex + 1;
  const accuracy = attempted > 0 ? Math.round((correctAnswers / attempted) * 100) : 0;
  const totalTime = section.timeLimit || (totalQuestions * 60); // Default 1 minute per question



  return {
    id: section.id,
    name: section.name,
    type: section.type,
    score: totalMarks,
    totalMarks: section.totalMarks || (totalQuestions * 4),
    correctAnswers,
    wrongAnswers,
    unattempted,
    attempted,
    totalQuestions,
    accuracy,
    timeSpent,
    totalTime: totalTime * 60, // Convert to seconds
    startQuestion: section.startQuestion,
    endQuestion: section.endQuestion
  };
};

/**
 * Check if user answer is correct
 * @param {Object} question - Question object
 * @param {any} userAnswer - User's answer
 * @returns {boolean} Whether answer is correct
 */
const checkAnswerCorrectness = (question, userAnswer) => {
  if (!question || userAnswer === null || userAnswer === undefined) {
    return false;
  }

  switch (question.type) {
    case 'multiple-choice':
      return userAnswer === question.correctAnswer;
    
    case 'multiple-choice-multi':
      if (Array.isArray(userAnswer) && Array.isArray(question.correctAnswer)) {
        return userAnswer.length === question.correctAnswer.length &&
               userAnswer.every(ans => question.correctAnswer.includes(ans));
      }
      return false;
    
    case 'integer':
    case 'floating-point':
      return parseFloat(userAnswer) === parseFloat(question.correctAnswer);
    
    case 'subjective':
      // For subjective questions, you might want to implement partial marking
      return userAnswer.toLowerCase().trim() === question.correctAnswer.toLowerCase().trim();
    
    default:
      return userAnswer === question.correctAnswer;
  }
};

/**
 * Generate default sectional results when no configuration is available
 * @param {Object} overallResults - Overall test results
 * @returns {Array} Default sectional results
 */
const generateDefaultSectionalResults = (overallResults) => {
  // Return empty array instead of default sections
  return [];
};

/**
 * Calculate sectional results from stored test data when sectional results are missing
 * @param {Object} testConfig - Test configuration with sections
 * @param {Array} questions - Questions array with user answers and correctness
 * @param {Object} overallResults - Overall test results
 * @returns {Array} Sectional results array
 */
export const calculateSectionalResultsFromStoredData = (testConfig, questions, overallResults) => {
  if (!testConfig || !testConfig.sections || !questions || questions.length === 0) {
    return [];
  }

  const sectionalResults = [];

  testConfig.sections.forEach(section => {
    const startIndex = parseInt(section.startQuestion) - 1;
    const endIndex = parseInt(section.endQuestion) - 1;
    
    // Get questions for this section
    const sectionQuestions = questions.slice(startIndex, endIndex + 1);
    
    let correctAnswers = 0;
    let wrongAnswers = 0;
    let unattempted = 0;
    let timeSpent = 0;
    let totalMarks = 0;

    // Calculate section results from stored question data
    sectionQuestions.forEach((question, index) => {
      // First check if user attempted this question
      const userAnswer = question.userAnswer;
      const hasUserAnswer = userAnswer !== null && userAnswer !== undefined && userAnswer !== '' && userAnswer !== 'null' && userAnswer !== 'undefined';

      
      if (hasUserAnswer) {
        // User attempted this question - check if correct or wrong
        if (question.isCorrect === true) {
          correctAnswers++;
          totalMarks += question.markingScheme?.correct || 4;
        } else if (question.isCorrect === false) {
          wrongAnswers++;
          totalMarks += question.markingScheme?.incorrect || -1;
        } else {
          // isCorrect not available, check manually
          const isCorrect = checkAnswerCorrectness(question, userAnswer);
          if (isCorrect) {
            correctAnswers++;
            totalMarks += question.markingScheme?.correct || 4;
          } else {
            wrongAnswers++;
            totalMarks += question.markingScheme?.incorrect || -1;
          }
        }
      } else {
        // User did not attempt this question
        unattempted++;
        totalMarks += question.markingScheme?.unattempted || 0;
      }
      
      // Add time spent on this question
      if (question.timeSpent) {
        timeSpent += question.timeSpent;
      }
    });

    const attempted = correctAnswers + wrongAnswers;
    const totalQuestions = sectionQuestions.length;
    const accuracy = attempted > 0 ? Math.round((correctAnswers / attempted) * 100) : 0;
    const totalTime = section.timeLimit || (totalQuestions * 60); // Default 1 minute per question

    const sectionResult = {
      id: section.id,
      name: section.name,
      type: section.type,
      score: totalMarks,
      totalMarks: section.totalMarks || (totalQuestions * 4),
      correctAnswers,
      wrongAnswers,
      unattempted,
      attempted,
      totalQuestions,
      accuracy,
      timeSpent,
      totalTime: totalTime * 60, // Convert to seconds
      startQuestion: section.startQuestion,
      endQuestion: section.endQuestion
    };

    sectionalResults.push(sectionResult);
  });

  return sectionalResults;
};

/**
 * Get section information for a specific question number
 * @param {Object} testConfig - Test configuration
 * @param {number} questionNumber - Question number (1-based)
 * @returns {Object|null} Section information or null if not found
 */
export const getSectionForQuestion = (testConfig, questionNumber) => {
  if (!testConfig || !testConfig.sections) {
    return null;
  }

  return testConfig.sections.find(section => 
    questionNumber >= parseInt(section.startQuestion) && questionNumber <= parseInt(section.endQuestion)
  );
};

/**
 * Validate test configuration
 * @param {Object} testConfig - Test configuration
 * @param {number} totalQuestions - Total number of questions
 * @returns {Object} Validation result with isValid and errors
 */
export const validateTestConfig = (testConfig, totalQuestions) => {
  const errors = [];
  
  if (!testConfig || !testConfig.sections) {
    return { isValid: false, errors: ['No sections configuration found'] };
  }

  let totalCovered = 0;
  const usedRanges = new Set();

  testConfig.sections.forEach((section, index) => {
    // Check for required fields
    if (!section.name) {
      errors.push(`Section ${index + 1}: Name is required`);
    }
    if (!section.startQuestion || !section.endQuestion) {
      errors.push(`Section ${index + 1}: Question range is required`);
    }

    // Check for valid range
    if (section.startQuestion && section.endQuestion) {
      const start = parseInt(section.startQuestion);
      const end = parseInt(section.endQuestion);
      
      if (start > end) {
        errors.push(`Section ${index + 1}: Start question must be less than end question`);
      }
      if (start < 1 || end > totalQuestions) {
        errors.push(`Section ${index + 1}: Range must be between 1 and ${totalQuestions}`);
      }

      // Check for overlapping ranges
      const rangeKey = `${start}-${end}`;
      if (usedRanges.has(rangeKey)) {
        errors.push(`Section ${index + 1}: Question ranges cannot overlap`);
      }
      usedRanges.add(rangeKey);

      totalCovered += (end - start + 1);
    }
  });

  // Check if all questions are covered
  if (totalCovered !== totalQuestions) {
    errors.push(`Sections must cover exactly ${totalQuestions} questions. Currently covering ${totalCovered} questions.`);
  }

  return {
    isValid: errors.length === 0,
    errors
  };
};
